import { identity, isEmpty } from 'ramda'
import createSelector from 'selectorator'
import { RootState } from 'src/state/types'
import { TPartyEntity, TPartyTag } from '../party/types'
import {
  TInvoiceSliceForReporting,
  TPerformanceBucket,
  TReportFilterOption,
  TReportingProperty,
  TReportingState,
  TSuspenseListKey,
  TSuspenseListState,
  TSuspenseReferenceLookup,
} from './types'
import { getInvoiceTypesForSelect as getInvoiceTypesForSelectUtil } from './utils'

const getSlice = <K extends keyof TReportingState>(key: K) =>
  createSelector<RootState, TReportingState[K]>([`api.reporting.${key}`], identity)

const getInvoiceSlice = <K extends keyof TInvoiceSliceForReporting>(key: K) =>
  createSelector<RootState, TInvoiceSliceForReporting[K]>([`api.invoice.${key}`], identity)

export const getDepositSummaryStats = getSlice('depositSummaryStats')

export const getPerformanceSummary = getSlice('performanceSummary')

export const getProperties = getSlice('properties')

export const getParties = getSlice('parties')

export const getSuspenseAccountStats = getSlice('suspenseAccountStats')

export const getSuspenseAccountLists = getSlice('suspenseAccountLists')

export const getSuspenseReferenceLookups = getSlice('suspenseReferenceLookups')

export const getSuspenseReferenceLookup = createSelector<
  RootState,
  (reference?: string) => TSuspenseReferenceLookup | undefined
>(
  [getSuspenseReferenceLookups],
  (lookups: TReportingState['suspenseReferenceLookups']) => (reference?: string) =>
    reference ? lookups[reference] : undefined,
)

export const getSuspenseAccountList = createSelector<RootState, (list: TSuspenseListKey) => TSuspenseListState>(
  [getSuspenseAccountLists],
  (lists: TReportingState['suspenseAccountLists']) => (list: TSuspenseListKey) => lists[list],
)

export const getInvoiceTypes = getInvoiceSlice('invoiceTypes')

export const getMonthlyPerformanceSummary = createSelector<RootState, TPerformanceBucket[]>(
  [getPerformanceSummary],
  (summary: TReportingState['performanceSummary']) => summary?.monthly || [],
)

export const getPropertiesForSelect = createSelector<RootState, TReportFilterOption[]>(
  [getProperties],
  (properties: TReportingProperty[]) =>
    properties.map(property => ({
      value: property.id,
      label: property.AddressPrimaryText,
    })),
)

export const getPropertyById = createSelector<RootState, (id: string) => TReportingProperty | undefined>(
  [getProperties],
  (properties: TReportingProperty[]) => (id: string) => properties.find(p => p.id === id),
)

export const getOwnerById = createSelector<RootState, (id: string) => TPartyEntity | undefined>(
  [getParties],
  (parties: TReportingState['parties']) => (id: string) => Object.values(parties).find(p => p.id === id),
)

const hasTag = (party: TPartyEntity, tag: TPartyTag): boolean =>
  ((party.person ?? party.company)?.tags ?? []).includes(tag)

const toPartyOption = (party: TPartyEntity): TReportFilterOption => ({
  value: party.id,
  label:
    party.company && !isEmpty(party.company.tradingAs) && party.company.tradingAs !== undefined
      ? party.company.companyName + ' t/a ' + party.company.tradingAs
      : party.company
      ? party.company.companyName ?? ''
      : (party.person?.firstName ?? '') + ' ' + (party.person?.lastName ?? ''),
})

export const getOwnersForSelect = createSelector<RootState, TReportFilterOption[]>(
  [getParties],
  (parties: TReportingState['parties']) =>
    Object.values(parties)
      .filter(party => hasTag(party, 'Owner'))
      .map(toPartyOption),
)

export const getAgentsForSelect = createSelector<RootState, TReportFilterOption[]>(
  [getParties],
  (parties: TReportingState['parties']) =>
    Object.values(parties)
      .filter(party => hasTag(party, 'Agent'))
      .map(toPartyOption),
)

export const getInvoiceTypesForSelect = createSelector<RootState, TReportFilterOption[]>(
  [getInvoiceTypes],
  (invoiceTypes: TInvoiceSliceForReporting['invoiceTypes']) =>
    getInvoiceTypesForSelectUtil(Object.values(invoiceTypes)),
)
